<template>
  <div class="step1-container">
    <!-- 거래처 검색 UI (모달에서 가져온 UI) -->
    <div class="search-client-content">
      <div class="search-client-header">
        <div class="modal-title">{{ $t('workorder_search_client') }}</div>
      </div>
      
      <div class="search-input-container-wrapper">
        <div class="search-input-container">
          <i class="fa-solid fa-magnifying-glass"/>
          <input
            type="text"
            @keyup.enter="search"
            v-model="searchParam.condition"
            :placeholder="$t('workorder_search_placeholder')"
          >
          <button class="search" @click="search">{{ $t('workorder_search_btn') }}</button>
        </div>
      
        <div class="list-container">
          <div class="list-items" v-if="result.list.length > 0">
            <div
              v-for="(item, index) in result.list"
              :key="index"
              class="list-item"
              :class="item.isSelected ? 'selected' : ''"
              @click="chooseItem(index)"
            >
              <a-typography-text :ellipsis="true" style="width: 100%;">
                <div class="item-left">
                  {{ item.name }}<span class="cno">({{ item.convertedHospitalInfo }})</span>
                </div>
              </a-typography-text>
              <div class="item-right" v-if="item.isSelected">
                <i class="fa-solid fa-check"/>
              </div>
            </div>
          </div>
          <div
            v-else
            style="align-items: center; justify-content: center; display: flex; margin-top: 164px;"
          >
            <no-result
              :colspan="10"
              :message="$t('workorder_search_no_result')"
              :height="'auto'"
              :noIcon="true"
            />
          </div>
          <div
            v-if="$store.state.isLoading !== false || result.list.length === 0"
            class="page-wrapper"
          />
          <pagination
            v-if="$store.state.isLoading === false && result.list.length > 0"
            :page-info="paginationData"
            page-name="Step1ClientSearch"
            :pagePerBlock="5"
            @change-page="paginationHandler"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAccessToken, getProfile } from '@/utils/tokenStore';
import {
  businessNumberFormatter,
  phoneNumberFormatter,
  jwtDecoder,
  oneBtnDialog
} from '@/utils/utils';
import apiInstance from '@/axios';
import NoResult from '@/views/components/NoResult';
import Pagination from '@/views/components/Pagination';
import { localStorageEnums } from "@/enums/localStorage.enums";

export default {
  name: 'Step1',
  components: {
    NoResult,
    Pagination
  },
  data() {
    return {
      searchParam: {
        condition: '',
      },
      paginationData: {
        listSize: 0,
        rowsPerPage: 10,
        currentPage: 1,
        totalPages: 0,
      },
      selectedItem: null,
      selectedClient: null,
      result: {
        list: [],
        totalDocs: 0,
      },
      isSearched: false,
      searchType: 'HOSPITAL'
    }
  },
  computed: {
    formToParam() {
      const obj = {
        page: this.paginationData.currentPage,
        limit: 10,
        type: this.searchType
      };

      // 제조사(기공사)의 진료과목과 locale을 전달
      if (this.searchType === 'HOSPITAL') {
        try {
          const companyInfo = JSON.parse(getProfile(localStorageEnums.CompanyInfo));
          if (companyInfo?.medicalDept && Array.isArray(companyInfo.medicalDept) && companyInfo.medicalDept.length > 0) {
            obj.medicalDept = companyInfo.medicalDept;
          }
          // locale 필터링: 제조사의 locale에 맞는 병원만 조회
          if (companyInfo?.locale) {
            obj.locale = companyInfo.locale;
          }
        } catch (error) {
          console.error('CompanyInfo 파싱 오류:', error);
        }
      }

      if (this.searchParam?.condition) {
        obj.search = isNaN(this.searchParam.condition) ? 'name' : 'cNo';
        obj.condition = this.searchParam.condition;
      }

      return obj;
    }
  },
  mounted() {
    // 초기 데이터 로드
    this.isSearched = true;
    this.getClientList();
  },
  methods: {
    formatBusinessNumber(value) {
      return businessNumberFormatter(value);
    },
    formatPhoneNumber(value) {
      return phoneNumberFormatter(value);
    },
    getAddressText(item) {
      let address = '';
      if (item.bizAddr) {
        address = item.bizAddr;
      } else if (item.addrOne && item.addrTwo && item.addrThree) {
        address = `${item.addrOne} ${item.addrTwo} ${item.addrThree}`;
      }
      if (item.addrDetail) {
        address += ` ${item.addrDetail}`;
      }
      return address.trim();
    },
    joinHospitalInfo(item) {
      const array = [
        item.ceo,
        businessNumberFormatter(item.cNo),
      ];

      let address = '';
      if (item.bizAddr) {
        address = item.bizAddr;
      } else if (item.addrOne && item.addrTwo && item.addrThree) {
        address = `${item.addrOne} ${item.addrTwo} ${item.addrThree}`;
      }

      if (item.addrDetail) address += ` ${item.addrDetail}`;

      if (address) array.push(address)

      return array.join(', ');
    },
    async search() {
      this.paginationData.currentPage = 1;
      this.isSearched = true;
      await this.getClientList();
    },
    async getClientList() {
      this.$store.commit('setIsLoading', true);
      try {
        const response = await apiInstance.post('/companies/list/contract/v2', this.formToParam);

        if (response.data.message) {
          await oneBtnDialog(this.$t('workorder_search_err_title'), this.$t('workorder_search_err_special_char'), false);
          return;
        }

        // 특정 사용자의 경우 필터링 (임시 코드)
        if (jwtDecoder(getAccessToken()) === process.env.VUE_APP_POINT_IMPLANT) {
          this.result.list = response.data.docs.filter(l => l.name === '서티치과');
        } else {
          this.result.list = response.data.docs;
        }

        this.result.totalDocs = response.data.totalDocs;
        this.paginationData.totalPages = response.data.totalPages;

         // 선택 상태 초기화 및 convertedHospitalInfo 생성
         this.result.list.forEach(item => {
           item.isSelected = false;
           item.convertedHospitalInfo = this.joinHospitalInfo(item);
         });
      } catch (error) {
        console.error('거래처 검색 실패:', error);
        await oneBtnDialog(this.$t('workorder_search_fail_title'), this.$t('workorder_search_fail_msg'), false);
      } finally {
        this.$store.commit('setIsLoading', false);
      }
    },
    chooseItem(index) {
      // 이미 선택된 항목이면 무시
      if (this.result.list[index].isSelected) return;

      // 모든 항목의 선택 상태 초기화
      this.result.list.forEach((item, i) => {
        item.isSelected = (index === i);
      });

      // 선택된 항목 저장
      this.selectedItem = this.result.list[index];
      this.selectedClient = this.selectedItem;
      
      // 부모 컴포넌트에 선택 이벤트 전달
      this.$emit('select-client', this.selectedItem);
    },
    async paginationHandler(childPassParam) {
      this.paginationData.currentPage = childPassParam.page;
      await this.getClientList();
    },
    // 선택 상태 초기화
    resetSelection() {
      this.selectedItem = null;
      this.result.list.forEach(item => {
        item.isSelected = false;
      });
    },
    clearSelection() {
      this.selectedClient = null;
      this.selectedItem = null;
      this.result.list.forEach(item => {
        item.isSelected = false;
      });
      this.$emit('select-client', null);
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/search-client-modal.scss';

.step1-container {
  width: 100%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);


  // 모달 스타일을 그대로 유지하되 인라인으로 적용
  .search-client-content {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    background-color: white;
    border-radius: 14px;
    overflow: hidden;
    width: 100%;

    .search-client-header {
      width: 100%;
      padding: 0 20px;
      height: 52px;
      display: flex;
      align-items: center;
      justify-content: start;
      position: relative;
      border-bottom: 1px solid rgba(0, 0, 0, 0.10);
      color: #2F3337;
      font-size: 17px;
      font-weight: 700;
      padding: 20px;
    }

    .search-input-container-wrapper {
      width: 100%;
      padding: 10px 20px;

    }
    .search-input-container {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      border: 1px solid rgba(0, 0, 0, 0.20);
      padding: 0px 0px 0px 14px;
      overflow: hidden;
      

      i {
        color: #8D939A;
      }

      input {
        width: 100%;
        border: none;
        outline: none;
        min-height: 40px;
        padding: 0px 14px;

        &:focus {
          border: none;
          box-shadow: none;
        }

        &::placeholder {
          color: #8D939A;
        }
      }

      .search {
        overflow: hidden;
        width: 150px;
        background-color: #2F3337;
        color: white;
        border-top-left-radius: 0px;
        border-bottom-left-radius: 0px;
        font-size: 15px;
        font-weight: bold;
        border-radius: 0;
        height: 40px;
        padding: 0 20px;
        justify-content: center;
        align-items: center;
        border-radius: 8px;
      }

      color: #2F3337;
      box-shadow: none !important;
      border: 1px solid #D1D6DB;
      
      &:hover {
        outline: none;
        border: 1px solid #8D939A !important;
        box-shadow: none;
      }

      &:focus-within {
        outline: none;
        border: 1px solid #4B5158 !important;
        box-shadow: 0px 0px 0px 2px rgba(0, 0, 0, 0.15) !important;
      }
    }

    .list-container {
      width: 100%;
      min-height: 540px;
      border-radius: 8px;
      border: 1px solid rgba(0, 0, 0, 0.20);
      display: flex;
      overflow: hidden;
      align-items: center;
      flex-direction: column;
      justify-content: space-between;
      margin: 20px 0px;

      .list-items {
        width: 100%;
        overflow: hidden;

        .list-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 20px;
          gap: 8px;
          height: 48px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.20);
          color: #2F3337;

          .cno {
            margin-left: 10px;
            color: #ACB2B9;
          }

          .item-right {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            border-radius: 100%;
            background-color: #ffe414;

            i {
              font-size: 14px;
            }
          }

          &:hover {
            cursor: pointer;
          }
        }

        .list-item.selected {
          font-weight: 700;
          background-color: #FFFAE0;

          .cno {
            font-weight: 700;
          }
        }
      }
    }
  }

  .expiration-modal-footer {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: end;
    padding: 0 20px 12px 20px;
    gap: 10px;

    .contract-submit-btn {
      font-size: 15px;
      font-weight: bold;
      background-color: #ffe414;
      color: #2F3337;
      height: 40px;
      padding: 0 20px;
      justify-content: center;
      align-items: center;
      border-radius: 8px;
    }

    .contract-cancel-btn {
      font-size: 15px;
      font-weight: bold;
      color: #6E747C;
      height: 40px;
      padding: 0 20px;
      justify-content: center;
      align-items: center;
      border-radius: 8px;
    }
  }

  .page-wrapper {
    width: 100%;
    background-color: #F3F5F6;
    display: flex;
    align-items: center;
    margin-top: 0px;
    padding-top: 20px;
    height: 60px;
  }
}

@media (max-width: 768px) {
  .step1-container {
    .search-client-content {
      .search-input-container {
        margin: 16px;
      }

      .list-container {
        margin: 0 16px;
        min-height: 400px;
      }
    }

    .expiration-modal-footer {
      padding: 0 16px 12px 16px;
      flex-direction: column;
      gap: 8px;

      .contract-cancel-btn,
      .contract-submit-btn {
        width: 100%;
      }
    }
  }
}
</style>